import * as XLSX from 'xlsx';
import moment from 'moment';

/**
 * Shape of the raw API response returned by GetCustomerAgreements.
 * Only the fields used by the account-report feature are declared here;
 * the actual response may contain additional properties.
 */
export interface AgreementApiResponse {
  status?: number;
  data?: {
    customerFirstName?: string;
    customerLastName?: string;
    agreement?: Array<{
      customers?: Array<{ firstName?: string; lastName?: string }>;
    }>;
    accountReportResponse?: AccountReportPaymentRecord[];
  };
}

export interface SelectedAgreement {
  agreementNumber: string;
  agreementId: string | number;
}

// Summary returned by generateAccountReport for partial-failure reporting.
export interface GenerateAccountReportResult {
  requested: number;
  succeeded: SelectedAgreement[];
  failed: Array<{ agreement: SelectedAgreement; reason: string }>;
  downloaded: boolean;
}

/**
 * Interface for account report payment record
 * Matches the actual API accountReportResponse structure
 */
export interface AccountReportPaymentRecord {
  storeNumber: string;
  firstName: string;
  lastName: string;
  agreementNumber: string;
  agreementId: number;
  originalDueDate: string;
  nextDueDate: string | null;
  paymentDate: string;
  pastDueDays: number;
  rentalRevenue: number;
  totalTax: number;
  ldwFeeAmount: number;
  carriedLateFee: number;
  netLateFee: number;
  numberOfDaysFree: number | null;
  paymentAmount: number;
  totalCost: number;
  initialPayment: number;
  receiptId: number;
  createdDate: string;
  paymentMethod: string;
  customerId: number;
  remainingRent: number | string;
  remainingTax: number;
  totalRemaining: number;
}

/**
 * Format payment record for Excel with exact column names from requirement
 * For remaining_rent, remaining_tax, total_remaining: only show values for the first record
 * of each agreement, leave blank for subsequent records.
 */
const formatPaymentRecordForExcel = (
  record: AccountReportPaymentRecord,
  isFirstRecord: boolean
) => ({
  store_number: record.storeNumber,
  first_name: record.firstName,
  last_name: record.lastName,
  agreement_number: record.agreementNumber,
  agreement_id: record.agreementId,
  original_due_date: record.originalDueDate,
  next_due_date: record.nextDueDate ?? '',
  payment_date: record.paymentDate,
  past_due_days: record.pastDueDays,
  rental_revenue: record.rentalRevenue,
  total_tax: record.totalTax,
  ldw_fee_amount: record.ldwFeeAmount,
  carried_late_fee: record.carriedLateFee,
  net_late_fee: record.netLateFee,
  number_of_days_free: record.numberOfDaysFree ?? '',
  payment_amount: record.paymentAmount,
  total_cost: record.totalCost,
  initial_payment: record.initialPayment,
  receipt_id: record.receiptId,
  created_date: record.createdDate,
  payment_method: record.paymentMethod,
  customer_id: record.customerId,
  remaining_rent: isFirstRecord ? record.remainingRent : '',
  remaining_tax: isFirstRecord ? record.remainingTax : '',
  total_remaining: isFirstRecord ? record.totalRemaining : '',
});

/**
 * Extract customer name from agreement data
 * Looks for customerFirstName and customerLastName at the top level of the response
 */
const extractCustomerName = (
  activeApiResponse?: AgreementApiResponse,
  inactiveApiResponse?: AgreementApiResponse
): { firstName: string; lastName: string } => {
  try {
    // Try active agreements response first
    if (
      activeApiResponse?.data?.customerFirstName &&
      activeApiResponse?.data?.customerLastName
    ) {
      return {
        firstName: activeApiResponse.data.customerFirstName,
        lastName: activeApiResponse.data.customerLastName,
      };
    }

    // Try inactive agreements response
    if (
      inactiveApiResponse?.data?.customerFirstName &&
      inactiveApiResponse?.data?.customerLastName
    ) {
      return {
        firstName: inactiveApiResponse.data.customerFirstName,
        lastName: inactiveApiResponse.data.customerLastName,
      };
    }

    // Fallback: try to get from first customer in first agreement
    const customer = activeApiResponse?.data?.agreement?.[0]?.customers?.[0];
    if (customer?.firstName && customer?.lastName) {
      return {
        firstName: customer.firstName,
        lastName: customer.lastName,
      };
    }

    // Default fallback
    return { firstName: 'Customer', lastName: 'Name' };
  } catch (_error: unknown) {
    return { firstName: 'Customer', lastName: 'Name' };
  }
};

/**
 * Extract account report data from API responses.
 *
 * The inActive=Y endpoint already returns ALL agreements (active + inactive),
 * so we use its accountReportResponse as the single source of truth.
 * The inActive=N (active-only) response is used only as a fallback when
 * the inActive=Y response is unavailable, preventing duplicate records.
 */
export const extractAccountReportData = (
  activeResponse?: AgreementApiResponse,
  inactiveResponse?: AgreementApiResponse
): AccountReportPaymentRecord[] => {
  try {
    // Prefer inActive=Y response — it contains records for ALL agreements
    if (
      inactiveResponse?.status === 200 &&
      inactiveResponse?.data?.accountReportResponse &&
      inactiveResponse.data.accountReportResponse.length > 0
    ) {
      return inactiveResponse.data.accountReportResponse;
    }

    // Fallback to inActive=N (active-only) if inActive=Y has no data
    if (
      activeResponse?.status === 200 &&
      activeResponse?.data?.accountReportResponse
    ) {
      return activeResponse.data.accountReportResponse;
    }

    return [];
  } catch (_error: unknown) {
    return [];
  }
};

/**
 * Get the set of agreement IDs and agreement numbers that have data in accountReportResponse.
 * Used to filter the agreements list in the UI so only agreements with report data are shown.
 */
export const getAgreementsWithReportData = (
  activeApiResponse?: AgreementApiResponse,
  inactiveApiResponse?: AgreementApiResponse
): { agreementIds: Set<string>; agreementNumbers: Set<string> } => {
  const allData = extractAccountReportData(
    activeApiResponse,
    inactiveApiResponse
  );
  const agreementIds = new Set<string>();
  const agreementNumbers = new Set<string>();

  allData.forEach((record) => {
    if (record.agreementId != null) {
      agreementIds.add(String(record.agreementId));
    }
    if (record.agreementNumber) {
      agreementNumbers.add(record.agreementNumber);
    }
  });

  return { agreementIds, agreementNumbers };
};

/**
 * Get payment records for a specific agreement from API response data
 */
const getPaymentRecordsForAgreement = (
  agreement: SelectedAgreement,
  activeApiResponse?: AgreementApiResponse,
  inactiveApiResponse?: AgreementApiResponse
): AccountReportPaymentRecord[] => {
  const allData = extractAccountReportData(
    activeApiResponse,
    inactiveApiResponse
  );
  const agreementIdNum =
    typeof agreement.agreementId === 'string'
      ? parseInt(agreement.agreementId, 10)
      : agreement.agreementId;

  const filteredData = allData.filter(
    (record) =>
      record.agreementId === agreementIdNum ||
      record.agreementNumber === agreement.agreementNumber
  );

  if (filteredData.length === 0) {
    throw new Error(
      `No payment records found for agreement ${agreement.agreementNumber}`
    );
  }

  return filteredData;
};

/**
 * Create a worksheet for a single agreement and append it to the workbook.
 * Returns true if a sheet was added, false otherwise.
 */
const addAgreementSheet = (
  wb: XLSX.WorkBook,
  agreement: SelectedAgreement,
  activeApiResponse?: AgreementApiResponse,
  inactiveApiResponse?: AgreementApiResponse
): boolean => {
  const paymentRecords = getPaymentRecordsForAgreement(
    agreement,
    activeApiResponse,
    inactiveApiResponse
  );

  if (!paymentRecords || paymentRecords.length === 0) {
    return false;
  }

  const formattedData = paymentRecords.map((record, index) =>
    formatPaymentRecordForExcel(record, index === 0)
  );

  const ws = XLSX.utils.json_to_sheet(formattedData);
  const columnKeys = Object.keys(formattedData[0]);
  ws['!cols'] = columnKeys.map(() => ({ wch: 20 }));

  const sheetName = agreement.agreementNumber.substring(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return true;
};

/**
 * Trigger file download with fallback to blob method.
 */
const downloadWorkbook = (wb: XLSX.WorkBook, filename: string): void => {
  try {
    XLSX.writeFile(wb, filename);
  } catch (_writeError: unknown) {
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }
};

/**
 * Main function to generate account report Excel file
 * Creates one Excel file with multiple sheets - one sheet per agreement
 * File naming: FirstName_LastName_account_report_CustomerID.xlsx
 *
 * @param customerId - Customer ID
 * @param selectedAgreements - List of selected agreements
 * @param activeApiResponse - Active agreements API response
 * @param inactiveApiResponse - Inactive agreements API response
 * @param customerFirstName - Customer's first name for filename
 * @param customerLastName - Customer's last name for filename
 */
export const generateAccountReport = async (
  customerId: string | number,
  selectedAgreements: SelectedAgreement[],
  activeApiResponse?: AgreementApiResponse,
  inactiveApiResponse?: AgreementApiResponse,
  customerFirstName?: string,
  customerLastName?: string
): Promise<GenerateAccountReportResult> => {
  if (!selectedAgreements || selectedAgreements.length === 0) {
    throw new Error(
      'No agreements selected. Please select at least one agreement.'
    );
  }

  if (!customerId) {
    throw new Error('Customer ID is required to generate the report.');
  }

  const wb = XLSX.utils.book_new();

  const extractedName = extractCustomerName(
    activeApiResponse,
    inactiveApiResponse
  );
  const firstName = customerFirstName || extractedName.firstName;
  const lastName = customerLastName || extractedName.lastName;

  const succeeded: SelectedAgreement[] = [];
  const failed: GenerateAccountReportResult['failed'] = [];

  for (const agreement of selectedAgreements) {
    try {
      const added = addAgreementSheet(
        wb,
        agreement,
        activeApiResponse,
        inactiveApiResponse
      );
      if (added) {
        succeeded.push(agreement);
      } else {
        failed.push({
          agreement,
          reason: 'No payment records returned for this agreement.',
        });
      }
    } catch (agreementError: unknown) {
      const reason =
        agreementError instanceof Error
          ? agreementError.message
          : 'Unknown error while adding agreement sheet.';
      failed.push({ agreement, reason });
    }
  }

  if (wb.SheetNames.length === 0) {
    throw new Error(
      'No data available to generate the report. Please try again.'
    );
  }

  const dateStr = moment().format('YYYY-MM-DD');
  const filename = `${firstName}_${lastName}_account_report_${customerId}_${dateStr}.xlsx`;

  downloadWorkbook(wb, filename);

  return {
    requested: selectedAgreements.length,
    succeeded,
    failed,
    downloaded: true,
  };
};
